import * as TruckActions from '../actions/truck';
import TruckService from '../../ports/TruckService';
import { apiCall } from '../actions/api-call';
import { Novel } from '../middlewares/novels';
import { Action } from '../actions';
import { toast } from '../actions/toast';
import { ToastType } from '../../models/utils';
import { ui } from '../actions/ui-options';
import { TruckStatus } from '../../models/truck';
import momentTimezone from 'moment-timezone';
import Provider from '../../ports/Provider';
import { Location } from '../../models/location';
import { getPalletBoxesFromShipmentGroup } from '../../utils/shipment-groups';

export interface TruckNovels {
  getTrucksNovel: Novel<TruckActions.GetTrucksRequestAction, Action>;
  createTruckNovel: Novel<TruckActions.CreateTruckRequestAction, Action>;
  deleteTruckNovel: Novel<TruckActions.DeleteTruckRequestAction, Action>;
  generateManifestNovel: Novel<TruckActions.GenerateManifestRequestAction, Action>;
  patchTruckNovel: Novel<TruckActions.PatchTruckRequestAction, Action>;
  loadTruckNovel: Novel<TruckActions.LoadTruckRequestAction, Action>;
  loadShipmentGroupNovel: Novel<TruckActions.LoadShipmentGroupRequestAction, Action>;
}

export const truckNovelsCreator = (
  truckService: TruckService,
  locationProvider: Provider<Location>,
): TruckNovels => {
  const getTrucksNovel = async (
    { data }: TruckActions.GetTrucksRequestAction,
    dispatch: (action: Action) => void,
  ) => {
    try {
      dispatch(apiCall());
      const trucks = await truckService.getTrucks(data);
      dispatch(apiCall.success());
      dispatch(TruckActions.getTrucks.success(trucks));
    } catch ({ message, details, correlationId }) {
      dispatch(apiCall.failure(message));
      dispatch(TruckActions.getTrucks.failure(message));
      if (correlationId && details) {
        dispatch(toast.push({
          correlationId,
          details,
          type: ToastType.DANGER,
          message: 'An error occured retrieving the trucks',
          links: [],
        }));
      } else {
        dispatch(toast.push({
          type: ToastType.DANGER,
          message: 'An error occured retrieving the trucks',
          details: message,
          links: [],
        }));
      }
    }
  };

  const createTruckNovel = async (
    { data }: TruckActions.CreateTruckRequestAction,
    dispatch: (action: Action) => void,
  ) => {
    try {
      dispatch(ui.lock());
      dispatch(apiCall());
      const truck = await truckService.createTruck(data);
      dispatch(apiCall.success());
      dispatch(TruckActions.createTruck.success(truck));
    } catch ({ message, details, correlationId }) {
      dispatch(apiCall.failure(message));
      dispatch(TruckActions.createTruck.failure(message));
      if (correlationId && details) {
        dispatch(toast.push({
          correlationId,
          details,
          type: ToastType.DANGER,
          message: `An error occured creating the truck '${data.name}'`,
          links: [],
        }));
      } else {
        dispatch(toast.push({
          type: ToastType.DANGER,
          message: `An error occured creating the truck '${data.name}'`,
          details: message,
          links: [],
        }));
      }
    } finally {
      dispatch(ui.unlock());
    }
  };

  const deleteTruckNovel = async (
    { data }: TruckActions.DeleteTruckRequestAction,
    dispatch: (action: Action) => void,
  ) => {
    try {
      dispatch(ui.lock());
      dispatch(apiCall());
      await truckService.deleteTruck(data);
      dispatch(apiCall.success());
      dispatch(TruckActions.deleteTruck.success(data));
    } catch (error: any) {
      const { message, details, correlationId } = error as {
        message: string;
        details: string;
        correlationId: string;
      };
      dispatch(apiCall.failure(message));
      dispatch(TruckActions.deleteTruck.failure(message));
      if (correlationId && details) {
        dispatch(toast.push({
          correlationId,
          details,
          type: ToastType.DANGER,
          message: `An error occured deleting the truck '${data.name}'`,
          links: [],
        }));
      } else {
        dispatch(toast.push({
          type: ToastType.DANGER,
          message: `An error occured deleting the truck '${data.name}'`,
          details: message,
          links: [],
        }));
      }
    } finally {
      dispatch(ui.unlock());
    }
  };

  const generateManifestNovel = async (
    { data }: TruckActions.GenerateManifestRequestAction,
    dispatch: (action: Action) => void,
  ) => {
    try {
      dispatch(ui.lock());
      dispatch(apiCall());
      await truckService.manifestTruck(data);
      dispatch(apiCall.success());
      dispatch(TruckActions.generateManifest.success({
        ...data,
        status: TruckStatus.CLOSED,
      }));
    } catch ({ message, details, correlationId }) {
      dispatch(apiCall.failure(message));
      dispatch(TruckActions.generateManifest.failure(message));
      if (correlationId && details) {
        dispatch(toast.push({
          correlationId,
          details,
          type: ToastType.DANGER,
          message: `An error occured manifesting the truck '${data.name}'`,
          links: [
            { label: 'Go to truck', href: `#truck-${data.id}` },
          ],
        }));
      } else {
        dispatch(toast.push({
          type: ToastType.DANGER,
          message: `An error occured manifesting the truck '${data.name}'`,
          details: message,
          links: [
            { label: 'Go to truck', href: `#truck-${data.id}` },
          ],
        }));
      }
    } finally {
      dispatch(ui.unlock());
    }
  };

  const patchTruckNovel = async (
    { data }: TruckActions.PatchTruckRequestAction,
    dispatch: (action: Action) => void,
  ) => {
    try {
      dispatch(ui.lock());
      dispatch(apiCall());
      const truck = await truckService.patchTruck(data);
      dispatch(apiCall.success());
      dispatch(TruckActions.patchTruck.success(truck));
    } catch ({ message, details, correlationId }) {
      dispatch(apiCall.failure(message));
      dispatch(TruckActions.patchTruck.failure(message));
      if (correlationId && details) {
        dispatch(toast.push({
          correlationId,
          details,
          type: ToastType.DANGER,
          message: `An error occured updating the truck '${data.name}'`,
          links: [
            { label: 'Go to truck', href: `#truck-${data.id}` },
          ],
        }));
      } else {
        dispatch(toast.push({
          type: ToastType.DANGER,
          message: `An error occured updating the truck '${data.name}'`,
          details: message,
          links: [
            { label: 'Go to truck', href: `#truck-${data.id}` },
          ],
        }));
      }
    } finally {
      dispatch(ui.unlock());
    }
  };

  const loadTruckNovel = async (
    {
      data: {
        palletBoxCount,
        truck,
        internalShipmentGroup,
      },
    }: TruckActions.LoadTruckRequestAction,
    dispatch: (action: Action) => void,
  ) => {
    try {
      dispatch(ui.lock());

      const shipmentCount = internalShipmentGroup.shipmentCount;
      const shipmentLimit = Math.floor(shipmentCount / palletBoxCount);
      let extra = shipmentCount % palletBoxCount;

      const timestamp = momentTimezone.tz(locationProvider().localeSettings.timezone).format('HH:mm:ss');

      let updatedTruck = truck;
      for (let i = 0; i < palletBoxCount; i++) {
        const suffix = palletBoxCount > 1 ? ` - ${i + 1}` : '';
        const palletBoxName = `${internalShipmentGroup.carrierAccountName} @${timestamp}${suffix}`;

        dispatch(apiCall());
        updatedTruck = await truckService.loadTruck(
          truck,
          internalShipmentGroup.id,
          palletBoxName,
          shipmentLimit + extra,
        );
        dispatch(apiCall.success());
        extra = 0;
      }

      dispatch(TruckActions.loadTruck.success({ internalShipmentGroup, palletBoxCount, truck: updatedTruck }));
    } catch ({ message, details, correlationId }) {
      dispatch(apiCall.failure(message));
      dispatch(TruckActions.loadTruck.failure(message));
      if (correlationId && details) {
        dispatch(toast.push({
          correlationId,
          details,
          type: ToastType.DANGER,
          message: 'An error occured loading the shipment group '
            + `'${internalShipmentGroup.carrierAccountName}' into the truck '${truck.name}'`,
          links: [
            { label: 'Go to truck', href: `#truck-${truck.id}` },
          ],
        }));
      } else {
        dispatch(toast.push({
          type: ToastType.DANGER,
          message: 'An error occured loading the shipment group '
            + `'${internalShipmentGroup.carrierAccountName}' into the truck '${truck.name}'`,
          details: message,
          links: [
            { label: 'Go to truck', href: `#truck-${truck.id}` },
          ],
        }));
      }
    } finally {
      dispatch(ui.unlock());
    }
  };

  const loadShipmentGroupNovel = async (
    { data: { palletBoxCounts, truck, shipmentGroup } }: TruckActions.LoadShipmentGroupRequestAction,
    dispatch: (action: Action) => void,
  ) => {
    dispatch(ui.lock());

    let updatedTruck = truck;
    const palletBoxes = getPalletBoxesFromShipmentGroup(
      shipmentGroup,
      palletBoxCounts,
      momentTimezone.tz(locationProvider().localeSettings.timezone),
    );
    for (const { id, carrierAccountName, palletBoxName, shipmentLimit } of palletBoxes) {
      try {
        dispatch(apiCall());
        updatedTruck = await truckService.loadTruck(truck, id, palletBoxName, shipmentLimit);
        dispatch(apiCall.success());
      } catch ({ message, details, correlationId }) {
        dispatch(apiCall.failure(message));
        dispatch(TruckActions.loadShipmentGroup.failure(message));
        if (correlationId && details) {
          dispatch(toast.push({
            correlationId,
            details,
            type: ToastType.DANGER,
            message: 'An error occured loading the shipment group '
              + `'${carrierAccountName}' into the truck '${truck.name}'`,
            links: [
              { label: 'Go to truck', href: `#truck-${truck.id}` },
            ],
          }));
        } else {
          dispatch(toast.push({
            type: ToastType.DANGER,
            message: 'An error occured loading the shipment group '
              + `'${carrierAccountName}' into the truck '${truck.name}'`,
            details: message,
            links: [
              { label: 'Go to truck', href: `#truck-${truck.id}` },
            ],
          }));
        }
        dispatch(ui.unlock());
        return;
      }
    }
    dispatch(TruckActions.loadShipmentGroup.success({ shipmentGroup, palletBoxCounts, truck: updatedTruck }));
    dispatch(ui.unlock());
  };

  return {
    createTruckNovel,
    deleteTruckNovel,
    getTrucksNovel,
    generateManifestNovel,
    patchTruckNovel,
    loadTruckNovel,
    loadShipmentGroupNovel,
  };
};
